import React, { useState, useEffect } from 'react';
import { Form, Input, Select, InputNumber, Row, Col, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const KEYWORD_TYPE_OPTIONS = {
    core_exact: '核心精准词',
    long_tail: '长尾词',
    brand: '品牌词',
    competitor: '竞品词',
};

const MATCH_TYPE_OPTIONS = {
    exact: '精准匹配',
    phrase: '词组匹配',
    broad: '广泛匹配',
};

const KeywordEdit: React.FC = () => {
    const { skuId, id } = useParams<{ skuId: string; id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<any>({
        onSave: async (data) => {
            if (isNew) {
                await api.post(`/operation-strategy/${skuId}/keywords/`, data);
                message.success('关键词已创建');
                navigate(`/operation-strategy/${skuId}`);
            } else {
                await api.put(`/operation-strategy/${skuId}/keywords/${id}/`, data);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        if (!isNew) {
            fetchKeyword();
        } else {
            form.setFieldsValue({
                keyword_type: 'core_exact',
                match_type: 'exact',
                priority: 3
            });
        }
    }, [id]);

    const fetchKeyword = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/operation-strategy/${skuId}/keywords/${id}/`);
            form.setFieldsValue(res.data);
        } catch (error) {
            message.error('加载失败');
            navigate(`/operation-strategy/${skuId}`);
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            await api.post(`/operation-strategy/${skuId}/keywords/`, values);
            message.success('关键词已创建');
            navigate(`/operation-strategy/${skuId}`);
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? '添加投放关键词' : '编辑关键词'}
            breadcrumbs={[
                { title: '运营策略', path: '/operation-strategy' },
                { title: 'SKU', path: `/operation-strategy/${skuId}` },
                { title: isNew ? '添加关键词' : '编辑关键词' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="keyword" label="关键词" rules={[{ required: true, message: '请输入关键词' }]}>
                            <Input.TextArea placeholder="输入关键词，多个关键词请换行分隔" rows={3} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="keyword_type" label="词性">
                            <Select options={Object.entries(KEYWORD_TYPE_OPTIONS).map(([value, label]) => ({ value, label }))} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="match_type" label="匹配类型">
                            <Select options={Object.entries(MATCH_TYPE_OPTIONS).map(([value, label]) => ({ value, label }))} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="conversion_rate" label="转化率">
                            <InputNumber min={0} max={1} step={0.01} placeholder="0.00" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="search_volume" label="搜索量">
                            <InputNumber min={0} placeholder="0" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="priority" label="优先级">
                            <InputNumber min={1} max={5} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="estimated_cpc" label="预估CPC">
                            <InputNumber min={0} step={0.01} placeholder="0.00" prefix="$" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="notes" label="备注">
                            <Input.TextArea rows={2} placeholder="可选备注" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </FormPageLayout>
    );
};

export default KeywordEdit;
